import { useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { subscribeTwin } from "@/twin/adapter";
import { runScenario } from "@/twin/calc";
import { createTwinRenderer } from "@/twin/renderer";
import { twin } from "../content/twin";
import { useContent, typeset } from "../ui/helpers";

/*
  DIGITAL TWIN DEMONSTRATION
  data      → src/twin/adapter.js  (sample data today, API later)
  numbers   → src/twin/calc.js     (one calculation feeds everything below)
  drawing   → src/twin/renderer.js (canvas; the station list is the accessible/2D view)
  text      → src/site/content/twin.js
*/

const fill = (s, vars) => s.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ""));

function StatusMark({ status, label }) {
  return (
    <span className={`st st-${status}`}>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        {status === "running" && <path d="M3.5 2.5 9.5 6l-6 3.5z" />}
        {status === "idle" && <path d="M3 2.5h2v7H3zM7 2.5h2v7H7z" />}
        {status === "stopped" && <path d="M2.5 2.5h7v7h-7z" />}
      </svg>
      {label}
    </span>
  );
}

function canUseCanvas() {
  try { return !!document.createElement("canvas").getContext("2d"); } catch { return false; }
}

export function DigitalTwin({ id = "twin" }) {
  const t = useContent(twin);
  const { lang } = useLang();
  const nf = useMemo(() => new Intl.NumberFormat(lang === "ko" ? "ko-KR" : "en-US", { maximumFractionDigits: 1 }), [lang]);
  const [feed, setFeed] = useState({ status: "loading" });
  const [lineId, setLineId] = useState(null);
  const [scenarioId, setScenarioId] = useState("current");
  const [selected, setSelected] = useState(null);
  const [found, setFound] = useState(false); // challenge: has the visitor found the bottleneck?
  const [guess, setGuess] = useState(null);
  const canvasRef = useRef(null);
  const pickRef = useRef(() => {});
  const renderer = useRef(null);
  const [canvasOk] = useState(canUseCanvas);

  useEffect(() => subscribeTwin(setFeed), []);
  const snap = feed.snapshot;
  const activeLine = lineId || snap?.lines?.[0]?.id;

  const result = useMemo(() => (snap && snap.lines.length ? runScenario(snap, activeLine, scenarioId) : null), [snap, activeLine, scenarioId]);
  const view = result?.after; // what the floor looks like with the chosen scenario
  const revealed = found || scenarioId !== "current";
  const sel = view?.stations.find((s) => s.id === selected) || (revealed ? view?.bottleneck : null);

  // selecting a station also answers the challenge's first question
  const selectStation = (id) => {
    setSelected(id);
    if (!found && result) {
      setGuess(id);
      if (id === result.before.bottleneck.id) setFound(true);
    }
  };
  pickRef.current = selectStation;

  // canvas (created once the first data is ready, kept while the section exists)
  const hasView = !!view;
  useEffect(() => {
    if (!canvasOk || !canvasRef.current) return undefined;
    renderer.current = createTwinRenderer(canvasRef.current, { onSelect: (sid) => pickRef.current(sid) });
    return () => renderer.current?.destroy();
  }, [canvasOk, hasView]);

  useEffect(() => {
    if (!renderer.current || !view) return;
    renderer.current.setModel({
      lineId: activeLine,
      stations: view.stations.map((s) => ({ ...s, opLabel: s.op[lang] || s.op.en })),
      rate: view.rate,
      bottleneckId: revealed ? view.bottleneck.id : null,
      bundleSize: result.line.bundleSize,
      labels: { bottleneck: t.bottleneck, in: t.floorIn, out: t.floorOut },
    });
  }, [view, activeLine, lang, t, result, revealed]);
  useEffect(() => { renderer.current?.setSelected(sel?.id); }, [sel?.id, view]);

  const pickLine = (id) => { setLineId(id); setSelected(null); setScenarioId("current"); setFound(false); setGuess(null); };
  const reset = () => { setLineId(snap?.lines[0]?.id); setScenarioId("current"); setSelected(null); setFound(false); setGuess(null); };
  const reveal = () => { setFound(true); setSelected(result?.before.bottleneck.id); };

  const op = (s) => typeset(s.op[lang] || s.op.en, lang);
  const lineName = result ? result.line.name[lang] || result.line.name.en : "";
  const u = t.units;
  const pct = (x) => `${Math.round(x * 100)}%`;
  const at = (a) => (a.targetAt.withinShift ? `${a.targetAt.clock}\u00A0· ${t.metrics.inShift}` : Number.isFinite(a.targetAt.overtimeMin)
    ? `${a.targetAt.clock}\u00A0· ${overtime(a.targetAt.overtimeMin)}` : t.metrics.never);
  const overtime = (min) => { const h = Math.floor(min / 60), m = min % 60; return fill(h && m ? t.metrics.overtime : h ? t.metrics.overtimeH : t.metrics.overtimeM, { h, m }).replace(/(\d) /g, "$1\u00A0"); };
  const signed = (n, unit = "") => `${n > 0 ? "+" : n < 0 ? "−" : "±"}${nf.format(Math.abs(n))}${unit}`;

  /* ---------- states ---------- */
  const banner = (() => {
    if (feed.status === "stale") return { cls: "warn", text: fill(t.state.stale, { n: Math.round((Date.now() - feed.receivedAt) / 60000) }) };
    if (feed.status === "disconnected") return { cls: "err", text: t.state.disconnected };
    if (snap) return { cls: "", text: fill(t.state.sample, { time: snap.shift.now }) };
    return null;
  })();

  const maxCap = view ? Math.max(...view.stations.map((s) => s.effCap)) : 1;
  const sIndex = view && sel ? view.stations.findIndex((s) => s.id === sel.id) + 1 : 0;

  const rows = result && [
    [t.metrics.rate, `${nf.format(result.before.rate)} ${u.pcsH}`, `${nf.format(result.after.rate)} ${u.pcsH}`, signed(Math.round((result.after.rate - result.before.rate) * 10) / 10)],
    [t.metrics.output, `${nf.format(result.before.output)} ${u.pcs}`, `${nf.format(result.after.output)} ${u.pcs}`, "±0"],
    [t.metrics.projected, `${nf.format(result.before.projected)} ${u.pcs}`, `${nf.format(result.after.projected)} ${u.pcs}`, signed(result.after.projected - result.before.projected)],
    [t.metrics.gap, signed(result.before.gap, ` ${u.pcs}`), signed(result.after.gap, ` ${u.pcs}`), signed(result.after.gap - result.before.gap)],
    [t.metrics.targetAt, at(result.before), at(result.after), ""],
    // Hidden while the challenge is still asking the visitor to find it
    [t.metrics.bottleneck, revealed ? op(result.before.bottleneck) : t.mission.hidden, revealed ? op(result.after.bottleneck) : t.mission.hidden, ""],
    [t.metrics.balance, `${result.before.balance}%`, `${result.after.balance}%`, signed(result.after.balance - result.before.balance, " pt")],
  ];

  const mission = (() => {
    if (!result) return { step: 0, msg: "" };
    const m = t.mission;
    if (!found && scenarioId === "current") {
      const g = guess && view.stations.find((x) => x.id === guess);
      if (g && g.id !== result.before.bottleneck.id) {
        return { step: 0, msg: fill(m.wrong, { op: op(g), cap: nf.format(Math.round(g.effCap)), idle: Math.round((1 - g.utilization) * 100) }), tone: "hint" };
      }
      return { step: 0, msg: fill(m.start, { line: lineName }) };
    }
    if (scenarioId === "current") return { step: 1, msg: fill(m.found, { op: op(result.before.bottleneck), rate: nf.format(result.before.rate) }), tone: "ok" };
    if (!result.moves.length) return { step: 2, msg: fill(m.noHelp, { op: op(result.before.bottleneck) }), tone: "hint", again: true };
    return { step: 3, msg: fill(m.done, { after: nf.format(result.after.rate), before: nf.format(result.before.rate), clock: result.after.targetAt.clock, clock0: result.before.targetAt.clock, next: op(result.after.bottleneck) }), tone: "win", again: true };
  })();

  const moveText = (() => {
    if (!result) return "";
    if (result.scenario.moves === 0) return t.moves.none;
    if (result.moves.length === 0) return fill(t.moves.noHelp, { op: op(result.before.bottleneck) });
    return null;
  })();

  return (
    <section className="twin on-dark" id={id} aria-labelledby={`${id}-title`}>
      <div className="k-wrap">
        <div className="twin-head">
          <h2 id={`${id}-title`} className="h-sec">{t.title}</h2>
          <div>
            <p className="twin-intro">{t.intro}</p>
            <p className="twin-badge"><span aria-hidden="true" className="twin-badge-dot" />{t.badge}</p>
          </div>
        </div>

        {feed.status === "loading" && <p className="twin-state" role="status">{t.state.loading}</p>}
        {feed.status === "empty" && <p className="twin-state" role="status">{t.state.empty}</p>}

        {result && (
          <>
            <div className={`mission tone-${mission.tone || "base"}`}>
              <div className="mission-top">
                <span className="mission-label">{t.mission.label}</span>
                <ol className="mission-steps">
                  {t.mission.steps.map((x, i) => (
                    <li key={x} className={mission.step > i ? "done" : mission.step === i ? "on" : undefined}>
                      <i aria-hidden="true">{mission.step > i ? "✓" : i + 1}</i>{x}
                    </li>
                  ))}
                </ol>
              </div>
              <p className="mission-msg" aria-live="polite">{mission.msg}</p>
              <p className="mission-tip">{t.mission.tip}</p>
              <div className="mission-actions">
                {!revealed && <button type="button" className="tbtn" onClick={reveal}>{t.mission.reveal}</button>}
                {mission.again && (
                  <button type="button" className="tbtn" onClick={() => { const i = snap.lines.findIndex((l) => l.id === activeLine); pickLine(snap.lines[(i + 1) % snap.lines.length].id); }}>
                    {t.mission.again} →
                  </button>
                )}
              </div>
            </div>

            <div className="twin-controls">
              <fieldset>
                <legend>{t.line}</legend>
                <div className="seg">
                  {snap.lines.map((l) => (
                    <button key={l.id} type="button" aria-pressed={l.id === activeLine} onClick={() => pickLine(l.id)}>
                      <strong>{l.name[lang] || l.name.en}</strong><small>{typeset(l.style[lang] || l.style.en, lang)}</small>
                    </button>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend>{t.scenario}</legend>
                <div className="seg">
                  {snap.scenarios.map((s) => (
                    <button key={s.id} type="button" aria-pressed={s.id === scenarioId} onClick={() => setScenarioId(s.id)}
                      className={mission.step === 1 && s.moves === 1 ? "nudge" : undefined}>
                      <strong>{typeset(s.label[lang] || s.label.en, lang)}</strong>
                    </button>
                  ))}
                </div>
              </fieldset>
              <button type="button" className="k-btn k-btn-line twin-reset" onClick={reset}>{t.reset}</button>
            </div>

            {banner && <p className={`twin-banner ${banner.cls}`} role="status">{banner.text}</p>}

            <div className="score" aria-live="polite">
              <div><small>{t.score.rate}</small><b key={`r${view.rate}`} className="pop">{nf.format(view.rate)}<span> {u.pcsH}</span></b></div>
              <div><small>{fill(t.score.target, { target: nf.format(result.line.dailyTarget) })}</small><b key={`t${view.targetAt.clock}`} className={`pop${view.targetAt.withinShift ? " good" : ""}`}>{view.targetAt.clock}<span> {view.targetAt.withinShift ? t.metrics.inShift : Number.isFinite(view.targetAt.overtimeMin) ? overtime(view.targetAt.overtimeMin) : ""}</span></b></div>
              <div><small>{t.score.bottleneck}</small><b key={`b${revealed}${view.bottleneck.id}`} className={`pop${revealed ? " bn" : ""}`}>{revealed ? op(view.bottleneck) : t.mission.hidden}</b></div>
            </div>

            <div className="twin-main">
              {canvasOk && (
                <figure className="twin-stage">
                  <canvas ref={canvasRef} role="img" aria-label={fill(t.canvasLabel, { line: lineName, n: view.stations.length, op: revealed ? op(view.bottleneck) : t.mission.hidden })} />
                  <figcaption>
                    <span>{t.hint}</span>
                    <span className="legend">
                      {["running", "idle", "stopped"].map((k) => <StatusMark key={k} status={k} label={t.status[k]} />)}
                      <span className="st st-bn"><i aria-hidden="true" />{t.bottleneck}</span>
                    </span>
                  </figcaption>
                </figure>
              )}

              {!sel && <aside className="twin-detail twin-detail-empty"><p>{t.pick}</p></aside>}
              {sel && (
                <aside className="twin-detail" aria-live="polite">
                  <p className="twin-detail-k">{lineName} · {fill(t.detail.title, { n: String(sIndex).padStart(2, "0") })}</p>
                  <h3>{op(sel)}</h3>
                  <StatusMark status={sel.status} label={`${t.status[sel.status]}: ${t.statusHelp[sel.status]}`} />
                  <dl>
                    <div><dt>{t.detail.operators}</dt><dd>{sel.operators}{u.people}</dd></div>
                    <div><dt>{t.detail.smv}</dt><dd>{nf.format(sel.smv)} {u.minPc}</dd></div>
                    <div><dt>{t.detail.capacity}</dt><dd>{nf.format(Math.round(sel.effCap * 10) / 10)} {u.pcsH}</dd></div>
                    <div><dt>{t.detail.util}</dt><dd>{pct(sel.utilization)}</dd></div>
                    <div><dt>{t.detail.completed}</dt><dd>{nf.format(sel.completed)} {u.pcs}</dd></div>
                    <div><dt>{t.detail.wip}</dt><dd>{nf.format(sel.wipBefore)} {u.pcs}</dd></div>
                    <div><dt>{t.detail.downtime}</dt><dd>{sel.downtimeMin} {u.min}</dd></div>
                  </dl>
                  <p className={`twin-detail-note${revealed && sel.isBottleneck ? " bn" : ""}`}>
                    {!revealed ? t.mission.tip : sel.isBottleneck ? t.detail.isBottleneck : fill(t.detail.notBottleneck, { op: op(view.bottleneck) })}
                  </p>
                </aside>
              )}
            </div>

            <div className="twin-lower">
              <div className="twin-list">
                <h3>{fill(t.stationsTitle, { line: lineName })}</h3>
                <p className="twin-note">{t.stationsNote}</p>
                <ol>
                  {view.stations.map((s, i) => (
                    <li key={s.id}>
                      <button type="button" aria-pressed={sel?.id === s.id} onClick={() => selectStation(s.id)}
                        className={revealed && s.isBottleneck ? "is-bn" : undefined}>
                        <span className="n">{String(i + 1).padStart(2, "0")}</span>
                        <span className="name">{op(s)}{revealed && s.isBottleneck && <em>{t.bottleneck}</em>}</span>
                        <StatusMark status={s.status} label={t.status[s.status]} />
                        <span className="ops">{s.operators}<span className="sr">{` ${t.cols.ops}`}</span></span>
                        <span className="bar" aria-hidden="true"><i style={{ width: `${(s.effCap / maxCap) * 100}%` }} /><b style={{ left: `${(view.rate / maxCap) * 100}%` }} /></span>
                        <span className="cap">{nf.format(Math.round(s.effCap))}<small> {u.pcsH}</small></span>
                      </button>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="twin-compare">
                <h3>{t.metrics.title}</h3>
                <p className="twin-moves">
                  {moveText ?? (
                    <>
                      {fill(t.moves.did, { n: result.moves.length })}{" "}
                      {result.moves.map((m, k) => (
                        <span key={k} className="mv">{m.fromOp[lang] || m.fromOp.en} {t.moves.arrow} {m.toOp[lang] || m.toOp.en}</span>
                      ))}
                      {result.moves.length < result.scenario.moves && <span className="twin-note"> {fill(t.moves.fewer, { n: result.moves.length })}</span>}
                    </>
                  )}
                </p>
                <div className="tbl-scroll">
                  <table>
                    <thead><tr><th scope="col"><span className="sr">{t.metrics.title}</span></th><th scope="col">{t.metrics.before}</th><th scope="col">{t.metrics.after}</th><th scope="col">{t.metrics.change}</th></tr></thead>
                    <tbody>
                      {rows.map(([k, a, b, d]) => <tr key={k}><th scope="row">{k}</th><td>{a}</td><td>{b}</td><td>{d}</td></tr>)}
                      <tr className="row-target"><th scope="row">{t.metrics.target}</th><td colSpan={3}>{nf.format(result.before.target)} {u.pcs}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <details className="twin-assume">
              <summary>{t.assumptions.title}</summary>
              <ul>{t.assumptions.items.map((x) => <li key={x}>{x}</li>)}</ul>
              <p>{t.assumptions.future}</p>
            </details>
          </>
        )}
      </div>
    </section>
  );
}
